import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { searchData } from '@/lib/data';
import { dramas } from '@/mocks/dramas';
import { usePageMeta } from '@/hooks/usePageMeta';
import SearchBox from '@/components/feature/SearchBox';
import DramaCard from '@/components/feature/DramaCard';

const TYPES = [
  { key: 'all', label: '全部' },
  { key: 'drama', label: '电视剧' },
  { key: 'actor', label: '演员' },
  { key: 'news', label: '资讯' },
];

const hotKeywords = ['首尔夜曲', '悬疑', '宋康昊', '记忆迷宫', '李知恩'];

export default function Search() {
  const [params] = useSearchParams();
  const keyword = params.get('q') ?? '';
  const [type, setType] = useState('all');

  usePageMeta(
    keyword ? `“${keyword}”的搜索结果 - 韩剧` : '搜索韩剧 - 韩剧',
    `在韩剧中搜索 ${keyword || '剧集、演员与资讯'}，快速找到你想要的韩剧内容。`,
  );

  const result = useMemo(() => searchData(keyword), [keyword]);
  const total = result.dramas.length + result.actors.length + result.news.length;

  const showDrama = type === 'all' || type === 'drama';
  const showActor = type === 'all' || type === 'actor';
  const showNews = type === 'all' || type === 'news';

  return (
    <div className="w-full">
      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">搜索韩剧</h1>
          <div className="mt-5 max-w-2xl">
            <SearchBox defaultValue={keyword} />
          </div>
          {keyword && (
            <p className="mt-4 text-sm text-foreground-600">
              关键词「<span className="text-primary-600 font-medium">{keyword}</span>」共找到{' '}
              <span className="font-semibold text-foreground-950">{total}</span> 条结果
            </p>
          )}
        </div>
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
        {!keyword ? (
          <div className="rounded-lg border border-background-200 bg-background-50 p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
              <i className="ri-search-line text-2xl"></i>
            </div>
            <h2 className="mt-4 text-lg font-semibold text-foreground-900">输入关键词开始搜索</h2>
            <p className="mt-2 text-sm text-foreground-500">支持搜索剧名、演员姓名、题材与资讯关键词。</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <span className="text-sm text-foreground-500">热门搜索：</span>
              {hotKeywords.map((k) => (
                <Link
                  key={k}
                  to={`/search?q=${encodeURIComponent(k)}`}
                  className="cursor-pointer whitespace-nowrap text-sm bg-background-100 hover:bg-primary-100 hover:text-primary-700 text-foreground-700 px-3.5 py-1.5 rounded-full transition-colors"
                >
                  {k}
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-1 bg-background-100 border border-background-200 rounded-full p-1 mb-8 w-fit">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setType(t.key)}
                  className={`cursor-pointer whitespace-nowrap text-sm px-4 py-1.5 rounded-full transition-colors ${
                    type === t.key ? 'bg-primary-500 text-white font-medium' : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {total === 0 ? (
              <div className="rounded-lg border border-background-200 bg-background-100 py-20 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-background-200 flex items-center justify-center text-foreground-400">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </div>
                <h3 className="mt-4 text-lg font-semibold text-foreground-900">没有找到相关结果</h3>
                <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或从下面推荐开始浏览。</p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  {dramas.slice(0, 4).map((d) => (
                    <Link
                      key={d.id}
                      to={`/detail/${d.id}`}
                      className="cursor-pointer whitespace-nowrap text-sm border border-background-300 hover:border-primary-400 hover:text-primary-600 text-foreground-700 px-3.5 py-1.5 rounded-full transition-colors"
                    >
                      {d.title}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-10">
                {showDrama && result.dramas.length > 0 && (
                  <section>
                    <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4">
                      电视剧 <span className="text-sm font-normal text-foreground-400">({result.dramas.length})</span>
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                      {result.dramas.map((d) => (
                        <DramaCard key={d.id} drama={d} />
                      ))}
                    </div>
                  </section>
                )}

                {showActor && result.actors.length > 0 && (
                  <section>
                    <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4">
                      演员 <span className="text-sm font-normal text-foreground-400">({result.actors.length})</span>
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
                      {result.actors.map((a) => (
                        <Link
                          key={a.id}
                          to={`/actor/${a.id}`}
                          className="group cursor-pointer rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
                        >
                          <div className="w-full aspect-[3/4] overflow-hidden bg-background-200">
                            <img
                              src={a.photo}
                              alt={`${a.name} 演员`}
                              title={`${a.name} - 韩剧演员`}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="p-3">
                            <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">{a.name}</h3>
                            <p className="mt-1 text-xs text-foreground-500 truncate">{a.nameEn}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}

                {showNews && result.news.length > 0 && (
                  <section>
                    <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4">
                      资讯 <span className="text-sm font-normal text-foreground-400">({result.news.length})</span>
                    </h2>
                    <ul className="divide-y divide-background-200 rounded-lg border border-background-200 overflow-hidden">
                      {result.news.map((n) => (
                        <li key={n.id}>
                          <Link to="/news" className="flex items-center gap-4 px-5 py-4 hover:bg-background-100 transition-colors cursor-pointer group">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-accent-700 bg-accent-100 rounded-full px-2 py-0.5">{n.category}</span>
                                <span className="text-xs text-foreground-400">{n.date}</span>
                              </div>
                              <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">{n.title}</h3>
                              <p className="mt-1 text-xs text-foreground-500 line-clamp-1">{n.summary}</p>
                            </div>
                            <i className="ri-arrow-right-s-line text-xl text-foreground-300 group-hover:text-primary-500 transition-colors shrink-0"></i>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}